import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ArrowUpDown,
  Download,
  X,
  MessageCircle,
  Link2,
  Copy,
  Clock,
  AlertTriangle,
  PhoneCall,
  Sparkles,
  Loader2,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI_LEAD = [
  { value: 'nuovo', label: 'Nuovo', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  { value: 'da_contattare', label: 'Da contattare', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'contattato', label: 'Contattato', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'interessato', label: 'Interessato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'documenti_richiesti', label: 'Documenti richiesti', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { value: 'in_trattativa', label: 'In trattativa', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'convertito', label: 'Convertito', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'non_interessato', label: 'Non interessato', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'perso', label: 'Perso', color: 'bg-red-100 text-red-700 border-red-200' },
];

const PROVENIENZE = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'sito_web', label: 'Sito Web' },
  { value: 'passaparola', label: 'Passaparola' },
  { value: 'telefono', label: 'Telefono' },
  { value: 'referral', label: 'Referral' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'comparatore', label: 'Comparatore AI' },
  { value: 'form_esterno', label: 'Form Esterno' },
  { value: 'altro', label: 'Altro' },
];

const INTERESSI = [
  { value: 'luce', label: 'Luce' },
  { value: 'gas', label: 'Gas' },
  { value: 'dual', label: 'Dual (Luce + Gas)' },
  { value: 'fotovoltaico', label: 'Fotovoltaico' },
  { value: 'altro', label: 'Altro' },
];

const PRIORITA = [
  { value: 'bassa', label: 'Bassa', color: 'bg-slate-100 text-slate-600' },
  { value: 'media', label: 'Media', color: 'bg-amber-100 text-amber-600' },
  { value: 'alta', label: 'Alta', color: 'bg-red-100 text-red-600' },
];

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    stato: searchParams.get('stato') || '',
    provenienza: '',
    interesse: '',
    priorita: '',
    assegnato_a: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLead, setNewLead] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    tipo_cliente: 'privato',
    citta: '',
    provincia: '',
    provenienza: 'altro',
    interesse: 'dual',
    stato: 'nuovo',
    priorita: 'media',
    assegnato_a: '',
    note: ''
  });

  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [referralLink, setReferralLink] = useState('');
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showSendOffer, setShowSendOffer] = useState(false);
  const [portalUsers, setPortalUsers] = useState([]);
  const [offerForm, setOfferForm] = useState({
    portal_user_id: '', servizio: 'luce', offerta_nome: '', compagnia: '',
    compagnia_logo: '', prezzo_descrizione: '', risparmio_annuo: '', durata_mesi: '',
    descrizione: '', note_agente: '',
  });
  const [sendingOffer, setSendingOffer] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (showSendOffer) {
      axios.get(`${API_URL}/api/admin/portal-users`).then(r => setPortalUsers(r.data || [])).catch(() => setPortalUsers([]));
      axios.get(`${API_URL}/api/companies`).then(r => setCompanies(r.data || [])).catch(() => setCompanies([]));
    }
  }, [showSendOffer]);

  const sendPortalOffer = async () => {
    if (!offerForm.portal_user_id || !offerForm.offerta_nome || !offerForm.compagnia) {
      toast.error('Cliente, nome offerta e compagnia sono obbligatori');
      return;
    }
    setSendingOffer(true);
    try {
      const payload = {
        ...offerForm,
        risparmio_annuo: offerForm.risparmio_annuo ? Number(offerForm.risparmio_annuo) : null,
        durata_mesi: offerForm.durata_mesi ? Number(offerForm.durata_mesi) : null,
      };
      await axios.post(`${API_URL}/api/admin/proposte`, payload);
      toast.success('Offerta inviata al cliente! Email in partenza.');
      setShowSendOffer(false);
      setOfferForm({
        portal_user_id: '', servizio: 'luce', offerta_nome: '', compagnia: '',
        compagnia_logo: '', prezzo_descrizione: '', risparmio_annuo: '', durata_mesi: '',
        descrizione: '', note_agente: '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio offerta');
    } finally { setSendingOffer(false); }
  };

  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, [filters]);

  const generateReferralLink = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me/referral-link`);
      setReferralLink(res.data.link);
      setShowReferralModal(true);
    } catch (e) {
      toast.error('Errore nella generazione del link');
    }
  };

  const getScadenzaStyle = (scadenza) => {
    if (!scadenza) return null;
    const now = new Date();
    const scadDate = new Date(scadenza);
    const diffDays = Math.ceil((scadDate - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'bg-red-100 text-red-700 border-red-200';
    if (diffDays <= 60) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API_URL}/api/leads?${params}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Errore nel caricamento dei lead');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  const handleCreateLead = async () => {
    try {
      await axios.post(`${API_URL}/api/leads`, newLead);
      toast.success('Lead creato con successo');
      setShowCreateModal(false);
      setNewLead({
        nome: '',
        cognome: '',
        telefono: '',
        email: '',
        tipo_cliente: 'privato',
        citta: '',
        provincia: '',
        provenienza: 'altro',
        interesse: 'dual',
        stato: 'nuovo',
        priorita: 'media',
        assegnato_a: '',
        note: ''
      });
      fetchLeads();
    } catch (error) {
      toast.error('Errore nella creazione del lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo lead?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/leads/${leadId}`);
      toast.success('Lead eliminato');
      fetchLeads();
    } catch (error) {
      toast.error('Errore nell\'eliminazione del lead');
    }
  };

  const handleConvertLead = async (leadId) => {
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/convert`);
      toast.success('Lead convertito in cliente');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conversione');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/leads`);
      const blob = new Blob([response.data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    }
  };

  const getStatoStyle = (stato) => {
    return STATI_LEAD.find(s => s.value === stato)?.color || 'bg-slate-100 text-slate-700';
  };

  const getStatoLabel = (stato) => {
    return STATI_LEAD.find(s => s.value === stato)?.label || stato;
  };

  const getPrioritaStyle = (priorita) => {
    return PRIORITA.find(p => p.value === priorita)?.color || 'bg-slate-100 text-slate-600';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Lead</h1>
          <p className="text-slate-500 mt-1">{leads.length} lead totali</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={generateReferralLink} data-testid="generate-referral-btn">
            <Link2 className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Link </span>Quotazione
          </Button>
          {hasRole(['super_admin', 'admin']) && (
            <Button variant="outline" size="sm" onClick={() => setShowSendOffer(true)} data-testid="send-portal-offer-btn"
              className="border-cyan-400/40 text-cyan-700 hover:bg-cyan-50">
              <Sparkles className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Invia </span>Offerta Cliente
            </Button>
          )}
          {hasRole(['super_admin', 'admin']) && (
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-leads-btn">
              <Download className="w-4 h-4 mr-1.5" />
              Esporta
            </Button>
          )}
          <Button size="sm" onClick={() => setShowCreateModal(true)} data-testid="create-lead-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Nuovo Lead
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, telefono, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-leads-input"
                />
              </div>
              <Button type="submit" variant="secondary">Cerca</Button>
            </form>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-slate-100' : ''}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
              <Select 
                value={filters.stato || "all"} 
                onValueChange={(v) => setFilters({...filters, stato: v === "all" ? "" : v})}
              >
                <SelectTrigger data-testid="filter-stato">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {STATI_LEAD.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.provenienza || "all"} 
                onValueChange={(v) => setFilters({...filters, provenienza: v === "all" ? "" : v})}
              >
                <SelectTrigger data-testid="filter-provenienza">
                  <SelectValue placeholder="Provenienza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {PROVENIENZE.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.interesse || "all"} 
                onValueChange={(v) => setFilters({...filters, interesse: v === "all" ? "" : v})}
              >
                <SelectTrigger data-testid="filter-interesse">
                  <SelectValue placeholder="Interesse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {INTERESSI.map(i => (
                    <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.priorita || "all"} 
                onValueChange={(v) => setFilters({...filters, priorita: v === "all" ? "" : v})}
              >
                <SelectTrigger data-testid="filter-priorita">
                  <SelectValue placeholder="Priorità" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {PRIORITA.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasRole(['super_admin', 'admin', 'interno', 'back_office']) && (
                <Select 
                  value={filters.assegnato_a || "all"} 
                  onValueChange={(v) => setFilters({...filters, assegnato_a: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="filter-agente">
                    <SelectValue placeholder="Agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leads - Desktop Table + Mobile Cards */}
      <Card>
        <CardContent className="p-0">
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-4 py-3">Lead</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3">Interesse</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Scadenza</th>
                  <th className="text-left px-4 py-3">Assegnato a</th>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      Nessun lead trovato
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="table-row cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                            <span>{lead.nome} {lead.cognome}</span>
                            {lead.portal_user_id ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider border border-emerald-200" title="Lead dal Portale Clienti">
                                <Globe className="w-3 h-3" />Portale
                              </span>
                            ) : lead.provenienza === 'sito_web' ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-sky-100 text-sky-700 text-[10px] font-bold uppercase tracking-wider border border-sky-200" title="Lead dal Sito Web">
                                <Globe className="w-3 h-3" />Dal Web
                              </span>
                            ) : null}
                          </p>
                          <p className="text-sm text-slate-500">{lead.citta || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-3 h-3" />
                            {lead.telefono || '-'}
                            {lead.telefono && (
                              <a 
                                href={`https://wa.me/${lead.telefono.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                                  lead.vuole_contatto 
                                    ? `Ciao ${lead.nome}, ho visto il tuo interesse per l'offerta ${lead.interesse === 'gas' ? 'gas' : 'luce'}. In cosa posso esserti utile?`
                                    : `Ciao ${lead.nome}, sono un consulente F.lli Schiliro' Servizi. Posso aiutarti a risparmiare sulla bolletta!`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 rounded bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                                title="WhatsApp"
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                              </a>
                            )}
                            {lead.vuole_contatto && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium" title="Vuole essere contattato">
                                <PhoneCall className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          {lead.email && (
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-slate-700 capitalize">
                          {lead.interesse}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${getStatoStyle(lead.stato)}`}>
                          {getStatoLabel(lead.stato)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {lead.scadenza_condizioni ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getScadenzaStyle(lead.scadenza_condizioni)}`}>
                            <Clock className="w-3 h-3" />
                            {formatDate(lead.scadenza_condizioni)}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {lead.assegnato_a_nome || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            {lead.stato !== 'convertito' && (
                              <DropdownMenuItem onClick={() => handleConvertLead(lead.id)}>
                                <UserCheck className="w-4 h-4 mr-2" />
                                Converti in cliente
                              </DropdownMenuItem>
                            )}
                            {hasRole(['super_admin', 'admin']) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {leads.length === 0 ? (
              <p className="px-4 py-12 text-center text-slate-500">Nessun lead trovato</p>
            ) : (
              leads.map((lead) => (
                <div key={lead.id} className="px-4 py-3 active:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`lead-card-${lead.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{lead.nome} {lead.cognome}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                        {lead.telefono && <span>{lead.telefono}</span>}
                        {lead.citta && <span>{lead.citta}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatoStyle(lead.stato)}`}>
                        {getStatoLabel(lead.stato)}
                      </span>
                      {lead.vuole_contatto && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px]">
                          <PhoneCall className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-xs text-slate-400">
                    <span className="capitalize">{lead.interesse}</span>
                    <span>{formatDate(lead.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Lead Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newLead.nome}
                onChange={(e) => setNewLead({...newLead, nome: e.target.value})}
                placeholder="Mario"
                data-testid="new-lead-nome"
              />
            </div>
            <div className="space-y-2">
              <Label>Cognome *</Label>
              <Input
                value={newLead.cognome}
                onChange={(e) => setNewLead({...newLead, cognome: e.target.value})}
                placeholder="Rossi"
                data-testid="new-lead-cognome"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefono *</Label>
              <Input
                value={newLead.telefono}
                onChange={(e) => setNewLead({...newLead, telefono: e.target.value})}
                placeholder="+39 333 1234567"
                data-testid="new-lead-telefono"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                placeholder="mario.rossi@email.it"
                data-testid="new-lead-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Città</Label>
              <Input
                value={newLead.citta}
                onChange={(e) => setNewLead({...newLead, citta: e.target.value})}
                placeholder="Milano"
              />
            </div>
            <div className="space-y-2">
              <Label>Provincia</Label>
              <Input
                value={newLead.provincia}
                onChange={(e) => setNewLead({...newLead, provincia: e.target.value})}
                placeholder="MI"
                maxLength={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo Cliente</Label>
              <Select 
                value={newLead.tipo_cliente} 
                onValueChange={(v) => setNewLead({...newLead, tipo_cliente: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privato">Privato</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Provenienza</Label>
              <Select 
                value={newLead.provenienza} 
                onValueChange={(v) => setNewLead({...newLead, provenienza: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVENIENZE.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Interesse</Label>
              <Select 
                value={newLead.interesse} 
                onValueChange={(v) => setNewLead({...newLead, interesse: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERESSI.map(i => (
                    <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priorità</Label>
              <Select 
                value={newLead.priorita} 
                onValueChange={(v) => setNewLead({...newLead, priorita: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITA.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasRole(['super_admin', 'admin', 'interno', 'back_office']) && (
              <div className="space-y-2">
                <Label>Assegna a</Label>
                <Select 
                  value={newLead.assegnato_a || "none"} 
                  onValueChange={(v) => setNewLead({...newLead, assegnato_a: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label>Note</Label>
              <textarea
                className="w-full h-24 px-3 py-2 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950"
                value={newLead.note}
                onChange={(e) => setNewLead({...newLead, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateLead}
              disabled={!newLead.nome || !newLead.cognome || !newLead.telefono}
              data-testid="submit-new-lead-btn"
            >
              Crea Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Link Modal */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent data-testid="referral-link-modal">
          <DialogHeader>
            <DialogTitle>Link Quotazione Esterna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Condividi questo link con i tuoi clienti. Potranno caricare la bolletta e ricevere un preventivo automatico. Il lead verrà creato automaticamente nella tua lista.
            </p>
            <div className="flex gap-2">
              <Input value={referralLink} readOnly className="font-mono text-sm" data-testid="referral-link-input" />
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(referralLink);
                  toast.success('Link copiato!');
                }}
                data-testid="copy-referral-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                className="bg-green-600 hover:bg-green-700 flex-1"
                onClick={() => {
                  window.open(`https://wa.me/?text=${encodeURIComponent(`Scopri quanto puoi risparmiare sulla bolletta! Carica la tua bolletta qui: ${referralLink}`)}`, '_blank');
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Condividi su WhatsApp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Invia Offerta al Portale */}
      <Dialog open={showSendOffer} onOpenChange={setShowSendOffer}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-offer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-600" />
              Invia offerta al portale cliente
            </DialogTitle>
            <DialogDescription>
              Il cliente la riceverà nella sua dashboard + via email. Potrà confermare o rifiutare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente del portale *</Label>
              <select value={offerForm.portal_user_id}
                onChange={(e) => setOfferForm({ ...offerForm, portal_user_id: e.target.value })}
                data-testid="offer-portal-user"
                className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— Seleziona cliente —</option>
                {portalUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.nome} {u.cognome} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Servizio *</Label>
                <select value={offerForm.servizio}
                  onChange={(e) => setOfferForm({ ...offerForm, servizio: e.target.value })}
                  data-testid="offer-servizio"
                  className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="luce">Luce</option>
                  <option value="gas">Gas</option>
                  <option value="fibra">Fibra</option>
                  <option value="dual">Dual (Luce+Gas)</option>
                </select>
              </div>
              <div>
                <Label>Compagnia *</Label>
                <select value={offerForm.compagnia}
                  onChange={(e) => {
                    const c = companies.find(x => x.nome === e.target.value);
                    setOfferForm({ ...offerForm, compagnia: e.target.value, compagnia_logo: c?.logo_url || '' });
                  }}
                  data-testid="offer-compagnia"
                  className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">— Seleziona —</option>
                  {companies.map(c => <option key={c.id || c.nome} value={c.nome}>{c.nome}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Nome offerta *</Label>
              <input value={offerForm.offerta_nome}
                onChange={(e) => setOfferForm({ ...offerForm, offerta_nome: e.target.value })}
                data-testid="offer-nome"
                className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                placeholder="Es. Luce Fissa 24" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Prezzo</Label>
                <input value={offerForm.prezzo_descrizione}
                  onChange={(e) => setOfferForm({ ...offerForm, prezzo_descrizione: e.target.value })}
                  data-testid="offer-prezzo"
                  className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  placeholder="0,129 €/kWh" />
              </div>
              <div>
                <Label>Risparmio annuo (€)</Label>
                <input type="number" value={offerForm.risparmio_annuo}
                  onChange={(e) => setOfferForm({ ...offerForm, risparmio_annuo: e.target.value })}
                  data-testid="offer-risparmio"
                  className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  placeholder="320" />
              </div>
              <div>
                <Label>Durata (mesi)</Label>
                <input type="number" value={offerForm.durata_mesi}
                  onChange={(e) => setOfferForm({ ...offerForm, durata_mesi: e.target.value })}
                  data-testid="offer-durata"
                  className="w-full mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  placeholder="24" />
              </div>
            </div>
            <div>
              <Label>Descrizione per il cliente</Label>
              <textarea value={offerForm.descrizione}
                onChange={(e) => setOfferForm({ ...offerForm, descrizione: e.target.value })}
                data-testid="offer-descrizione" rows={3}
                className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="Dettagli dell'offerta visibili al cliente nel portale..." />
            </div>
            <div>
              <Label>Note dell'agente (opzionale)</Label>
              <textarea value={offerForm.note_agente}
                onChange={(e) => setOfferForm({ ...offerForm, note_agente: e.target.value })}
                data-testid="offer-note" rows={2}
                className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="Es. Consigliata perché..." />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSendOffer(false)}>Annulla</Button>
              <Button onClick={sendPortalOffer} disabled={sendingOffer} data-testid="send-offer-submit"
                className="bg-cyan-600 hover:bg-cyan-500 text-white">
                {sendingOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Sparkles className="w-4 h-4 mr-1.5" /> Invia al portale</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
